
import type {
  DomainGuid,
  EvaluatedStatus
} from '@/features/execute-calculation-set/overmind/calculation';

import powerTransformerSvg from '../assets/01_PowerTransformer.svg';
import powerTransformerBadSvg from '../assets/01_PowerTransformer_Bad.svg';
import powerTransformerGoodSvg from '../assets/01_PowerTransformer_Good.svg';
import powerTransformerSelectedSvg from '../assets/01_PowerTransformer_Selected.svg';
import powerTransformerSelectedBadSvg from '../assets/01_PowerTransformer_Selected_Bad.svg';
import powerTransformerSelectedGoodSvg from '../assets/01_PowerTransformer_Selected_Good.svg';
import powerTransformerSelectedWarningSvg from '../assets/01_PowerTransformer_Selected_Warning.svg';
import powerTransformerWarningSvg from '../assets/01_PowerTransformer_Warning.svg';
import externalNetworkInjectionSvg from '../assets/externalNetworkInjection.svg';
import externalNetworkInjectionSelectedSvg from '../assets/externalNetworkInjection_selected.svg';
import { icon } from 'leaflet';
import { HighlightStates } from '../utils/styleMapElement';

export function getExternalNetworkInjectionIcon(
  externalNetworkInjectionId: DomainGuid,
  selectedElementId: DomainGuid | undefined
) {
  const isSelected = externalNetworkInjectionId === selectedElementId;
  return isSelected
    ? selectedExternalNetworkInjectionIcon
    : externalNetworkInjectionIcon;
}

const externalNetworkInjectionIcon = icon({
  iconUrl: externalNetworkInjectionSvg,
  iconRetinaUrl: externalNetworkInjectionSvg,
  iconAnchor: [20, 20],
  iconSize: [30, 30],
  className: 'external-network-injection-icon-background'
});

const selectedExternalNetworkInjectionIcon = icon({
  iconUrl: externalNetworkInjectionSelectedSvg,
  iconRetinaUrl: externalNetworkInjectionSelectedSvg,
  iconAnchor: [21, 21],
  iconSize: [32, 32],
  className: 'selected-external-network-injection-icon-background '
});

export function getTransformerIcon(
  transformerId: DomainGuid,
  selectedElementId: DomainGuid | undefined,
  initiatingElementId: DomainGuid,
  statuses: HighlightStates,
  shouldUseInitiatingObjectHighlighting: boolean
) {
  return _getTransformerIcon({ shouldUseInitiatingObjectHighlighting })(
    transformerId,
    selectedElementId,
    initiatingElementId,
    statuses
  );
}

export function getTransformerIconUrl(
  transformerId: DomainGuid,
  selectedElementId: DomainGuid | undefined,
  initiatingElementId: DomainGuid,
  statuses: HighlightStates,
  shouldUseInitiatingObjectHighlighting: boolean
) {
  const icon = _getTransformerIcon({ shouldUseInitiatingObjectHighlighting })(
    transformerId,
    selectedElementId,
    initiatingElementId,
    statuses
  );
  return icon.options.iconUrl;
}

const powerTransformerIcon = icon({
  iconUrl: powerTransformerSvg,
  iconRetinaUrl: powerTransformerSvg,
  iconAnchor: [20, 20],
  iconSize: [20, 20]
});
const powerTransformerGoodIcon = icon({
  iconUrl: powerTransformerGoodSvg,
  iconRetinaUrl: powerTransformerGoodSvg,
  iconAnchor: [20, 20],
  iconSize: [20, 20]
});
const powerTransformerBadIcon = icon({
  iconUrl: powerTransformerBadSvg,
  iconRetinaUrl: powerTransformerBadSvg,
  iconAnchor: [20, 20],
  iconSize: [20, 20]
});
const powerTransformerWarningIcon = icon({
  iconUrl: powerTransformerWarningSvg,
  iconRetinaUrl: powerTransformerWarningSvg,
  iconAnchor: [20, 20],
  iconSize: [20, 20]
});
const selectedPowerTransformerIcon = icon({
  iconUrl: powerTransformerSelectedSvg,
  iconRetinaUrl: powerTransformerSelectedSvg,
  iconAnchor: [21, 21],
  iconSize: [22, 22]
});
const selectedPowerTransformerGoodIcon = icon({
  iconUrl: powerTransformerSelectedGoodSvg,
  iconRetinaUrl: powerTransformerSelectedGoodSvg,
  iconAnchor: [21, 21],
  iconSize: [22, 22]
});
const selectedPowerTransformerBadIcon = icon({
  iconUrl: powerTransformerSelectedBadSvg,
  iconRetinaUrl: powerTransformerSelectedBadSvg,
  iconAnchor: [21, 21],
  iconSize: [22, 22]
});
const selectedPowerTransformerWarningIcon = icon({
  iconUrl: powerTransformerSelectedWarningSvg,
  iconRetinaUrl: powerTransformerSelectedWarningSvg,
  iconAnchor: [21, 21],
  iconSize: [22, 22]
});

import initiatingSubstationSvg from '../assets/initiating-grid-element/initiating_01_PowerTransformer.svg';
import initiatingSubstationBadSvg from '../assets/initiating-grid-element/initiating_01_PowerTransformer_Bad.svg';
import initiatingSubstationGoodSvg from '../assets/initiating-grid-element/initiating_01_PowerTransformer_Good.svg';
import initiatingSubstationWarningSvg from '../assets/initiating-grid-element/initiating_01_PowerTransformer_Warning.svg';
import initiatingSubstationSelectedSvg from '../assets/initiating-grid-element/initiating_01_PowerTransformer_Selected.svg';
import initiatingSubstationSelectedBadSvg from '../assets/initiating-grid-element/initiating_01_PowerTransformer_Selected_Bad.svg';
import initiatingSubstationSelectedGoodSvg from '../assets/initiating-grid-element/initiating_01_PowerTransformer_Selected_Good.svg';
import initiatingSubstationSelectedWarningSvg from '../assets/initiating-grid-element/initiating_01_PowerTransformer_Selected_Warning.svg';

function createInitiatingSubstationIcons() {
  const powerTransformerIcon = icon({
    iconUrl: initiatingSubstationSvg,
    iconRetinaUrl: initiatingSubstationSvg,
    iconAnchor: [20, 20],
    iconSize: [30, 30]
  });
  const powerTransformerGoodIcon = icon({
    iconUrl: initiatingSubstationGoodSvg,
    iconRetinaUrl: initiatingSubstationGoodSvg,
    iconAnchor: [20, 20],
    iconSize: [30, 30]
  });
  const powerTransformerBadIcon = icon({
    iconUrl: initiatingSubstationBadSvg,
    iconRetinaUrl: initiatingSubstationBadSvg,
    iconAnchor: [20, 20],
    iconSize: [30, 30]
  });
  const powerTransformerWarningIcon = icon({
    iconUrl: initiatingSubstationWarningSvg,
    iconRetinaUrl: initiatingSubstationWarningSvg,
    iconAnchor: [20, 20],
    iconSize: [30, 30]
  });
  const selectedPowerTransformerIcon = icon({
    iconUrl: initiatingSubstationSelectedSvg,
    iconRetinaUrl: initiatingSubstationSelectedSvg,
    iconAnchor: [21, 21],
    iconSize: [32, 32]
  });
  const selectedPowerTransformerGoodIcon = icon({
    iconUrl: initiatingSubstationSelectedGoodSvg,
    iconRetinaUrl: initiatingSubstationSelectedGoodSvg,
    iconAnchor: [21, 21],
    iconSize: [32, 32]
  });
  const selectedPowerTransformerBadIcon = icon({
    iconUrl: initiatingSubstationSelectedBadSvg,
    iconRetinaUrl: initiatingSubstationSelectedBadSvg,
    iconAnchor: [21, 21],
    iconSize: [32, 32]
  });
  const selectedPowerTransformerWarningIcon = icon({
    iconUrl: initiatingSubstationSelectedWarningSvg,
    iconRetinaUrl: initiatingSubstationSelectedWarningSvg,
    iconAnchor: [21, 21],
    iconSize: [32, 32]
  });
  return {
    powerTransformerIcon,
    powerTransformerGoodIcon,
    powerTransformerBadIcon,
    powerTransformerWarningIcon,
    selectedPowerTransformerIcon,
    selectedPowerTransformerGoodIcon,
    selectedPowerTransformerBadIcon,
    selectedPowerTransformerWarningIcon
  }
}

const initiatingIcons = createInitiatingSubstationIcons();

export const createGetTransformerIcon = (
  shouldUseInitiatingObjectHighlighting: boolean
): GetTransformerIcon => {
  return _getTransformerIcon({shouldUseInitiatingObjectHighlighting})
}

export type GetTransformerIcon = ReturnType<typeof _getTransformerIcon>;

const _getTransformerIcon = (context: {shouldUseInitiatingObjectHighlighting: boolean}) => (
  transformerId: DomainGuid,
  selectedElementId: DomainGuid | undefined,
  initiatingElementId: DomainGuid,
  statuses: HighlightStates
) => {
  const isSelected = transformerId === selectedElementId;
  const isInitiating = transformerId === initiatingElementId && context.shouldUseInitiatingObjectHighlighting;
  const status: EvaluatedStatus | undefined =
    statuses.type === 'SOME_HIGHLIGHTED'
      ? statuses.context.highlighted[transformerId]
      : undefined;
  if (status === 'Good') {
    return isSelected
      ? isInitiating? initiatingIcons.selectedPowerTransformerGoodIcon : selectedPowerTransformerGoodIcon
      : isInitiating? initiatingIcons.powerTransformerGoodIcon : powerTransformerGoodIcon;
  }
  if (status === 'Bad') {
    return isSelected
    ? isInitiating? initiatingIcons.selectedPowerTransformerBadIcon : selectedPowerTransformerBadIcon
    : isInitiating? initiatingIcons.powerTransformerBadIcon : powerTransformerBadIcon;
  }
  if (status === 'Warning') {
    return isSelected
      ? isInitiating? initiatingIcons.selectedPowerTransformerWarningIcon : selectedPowerTransformerWarningIcon
      : isInitiating? initiatingIcons.powerTransformerWarningIcon : powerTransformerWarningIcon;
  }
  return isSelected ? selectedPowerTransformerIcon : powerTransformerIcon;
}
